import { EdgeDirectionType } from 'data/types';
import { getLiveOpeningDragPlacement } from 'utils/opening-drag-live-placement';
import { EdgeRunType } from 'utils/opening-position-types';
import { numericOpeningPosition } from 'utils/opening-position-value';

export type GridSpaceDropPlacement = {
  edge: EdgeDirectionType;
  offsetFt: number;
};

export type GridSpaceDropData = {
  type: 'grid-space-drop';
  spaceId: string;
  sectionId?: string;
  dropEdge: EdgeDirectionType | null;
  dropOffsetFt: number | undefined;
};

export function createGridSpaceDropData(
  spaceId: string,
  sectionId: string | undefined,
  placement: GridSpaceDropPlacement | null
): GridSpaceDropData {
  if (!placement) {
    return {
      type: 'grid-space-drop',
      spaceId,
      sectionId,
      dropEdge: null,
      dropOffsetFt: undefined
    };
  }

  return {
    type: 'grid-space-drop',
    spaceId,
    sectionId,
    dropEdge: placement.edge,
    dropOffsetFt: placement.offsetFt
  };
}

function liveOpeningDragPlacement(
  spaceId: string | undefined
): ReturnType<typeof getLiveOpeningDragPlacement> {
  return spaceId ? getLiveOpeningDragPlacement(spaceId) : undefined;
}

export function resolveOpeningDropEdge(
  overData: Pick<GridSpaceDropData, 'dropEdge' | 'spaceId'>
): EdgeDirectionType | null {
  return liveOpeningDragPlacement(overData.spaceId)?.edge ?? overData.dropEdge;
}

export function openingPlacementArgs(
  overData: Pick<GridSpaceDropData, 'dropOffsetFt' | 'spaceId'>
): { positionOnWall?: number } {
  const liveOffsetFt = liveOpeningDragPlacement(overData.spaceId)?.offsetFt;
  const positionOnWall = numericOpeningPosition(
    liveOffsetFt ?? overData.dropOffsetFt
  );

  if (positionOnWall === undefined) {
    return {};
  }

  return { positionOnWall };
}

export function openingPlacementRun(
  overData: Pick<GridSpaceDropData, 'spaceId'>
): EdgeRunType | undefined {
  return liveOpeningDragPlacement(overData.spaceId)?.run;
}

function isGridSpaceDropData(value: unknown): value is GridSpaceDropData {
  if (!value || typeof value !== 'object') {
    return false;
  }
  return (value as GridSpaceDropData).type === 'grid-space-drop';
}

export function gridSpaceDropDataFromCollision(collision: {
  id: string | number;
  data?: { droppableContainer?: { data?: { current?: unknown } } };
}): GridSpaceDropData | null {
  if (!String(collision.id).startsWith('drop-')) {
    return null;
  }
  const value = collision.data?.droppableContainer?.data?.current;
  return isGridSpaceDropData(value) ? value : null;
}

export function resolveGridSpaceDropData(
  over: { id: string | number; data: { current?: unknown } } | null,
  collisions?: Array<{
    id: string | number;
    data?: { droppableContainer?: { data?: { current?: unknown } } };
  }> | null
): GridSpaceDropData | null {
  if (over && String(over.id) === 'item-list-container') {
    return null;
  }

  if (
    over &&
    String(over.id).startsWith('drop-') &&
    isGridSpaceDropData(over.data.current)
  ) {
    return over.data.current;
  }

  const hit = collisions?.find((collision) =>
    Boolean(gridSpaceDropDataFromCollision(collision))
  );
  return hit ? gridSpaceDropDataFromCollision(hit) : null;
}
